/* eslint-disable @smarttools/rxjs/finnish */
import { Injectable } from '@angular/core';
import { BleClient } from '@capacitor-community/bluetooth-le';
import { createLogger } from '@hunting-nature/shared-core/logging';
import { concat, defer, EMPTY, from, merge, of, Subject, throwError, timer, type Observable } from 'rxjs';
import { catchError, concatMap, expand, ignoreElements, last, map, retry, switchMap, take, tap, timeout, toArray } from 'rxjs/operators';

import { CANNES_BLE_CONFIG } from '../interfaces/ble.interface';

/**
 * HTTP Status Code characteristic data-status flags (3rd byte of the notification).
 * Mirrors the firmware's `DataStatusFlags` (HttpProxyService.hpp).
 */
const DATA_STATUS = {
  BODY_RECEIVED: 0x04,
  BODY_TRUNCATED: 0x08,
} as const;

/**
 * Maximum entity-body payload per BLE write, in bytes.
 *
 * BlueZ enforces the 512-byte ATT maximum attribute value length, and the firmware
 * (`MAX_ENTITY_BODY_LENGTH = 512`) truncates/rejects anything larger. 510 matches the
 * chunk size in the firmware's own reference flow (HttpProxyService_ChunkedFlow.drawio)
 * and leaves headroom below the hard limit.
 */
const MAX_CHUNK_SIZE = 510;

interface ContentRange {
  start: number;
  end: number;
  total: number;
}

interface WriteChunk {
  bytes: Uint8Array;
  range: ContentRange | null;
}

interface ParsedStatus {
  code: number;
  dataStatus: number;
  truncated: boolean;
  empty: boolean;
}

/**
 * REST-over-BLE proxy for ZEISS Cannes devices.
 *
 * Implements the firmware's HTTP-over-GATT proxy (HttpProxyService), including the
 * Content-Range chunked transfer protocol required for payloads larger than 512 bytes
 * (e.g. the 544-byte device-signing challenge and the 512-byte counter-signature).
 *
 * Per request the flow is:
 *   enable STATUS_CODE notifications -> write URI -> [write body chunks | trigger] ->
 *   await status notification per round trip -> (GET) read body chunks with continuation.
 *
 * Chunked WRITE (body > 512 B): write the URI once, then for each chunk write the
 * `Range: "bytes start-end/total"` HTTP header, write the entity-body slice, trigger PUT,
 * and await the per-chunk acknowledgment (HTTP 202 | BODY_TRUNCATED) until the final
 * chunk yields the real response.
 *
 * Chunked READ (response > 512 B): the firmware returns the first 512 bytes with
 * HTTP 206 | BODY_TRUNCATED; the client re-triggers GET (without re-writing the URI) to
 * pull each continuation chunk until BODY_RECEIVED, then reassembles the full body.
 */
@Injectable({
  providedIn: 'root',
})
export class BleRestProxyService {
  private readonly textEncoder = new TextEncoder();
  private readonly textDecoder = new TextDecoder('utf-8');
  private readonly REQUEST_TIMEOUT_MS = 5_000;
  private readonly RETRY_COUNT = 2;
  private readonly RETRY_DELAY_MS = 0;
  private readonly log = createLogger('BleRestProxyService');

  // Resolved REST-proxy service UUID per device (firmware-dependent: 4f311823 vs legacy 4f310823).
  private readonly serviceUuidByDevice = new Map<string, string>();

  private readonly notificationStates = new Map<string, boolean>();
  // Bridges the STATUS_CODE notification callback into a reactive stream per device.
  private readonly notificationSubjects = new Map<string, Subject<DataView>>();

  /**
   * Discovers which GATT service exposes the REST-proxy characteristics on this device and caches it.
   * Supports firmware that moved the characteristics from 4f310823 to 4f311823.
   * Falls back to the candidate priority list, then to the default service UUID.
   */
  resolveServiceUuid$(deviceId: string): Observable<string> {
    return from(BleClient.getServices(deviceId)).pipe(
      map(services => {
        const uriChar = CANNES_BLE_CONFIG.CHARACTERISTICS.URI_ENDPOINT.toLowerCase();

        const serviceWithUri = services.find(service => service.characteristics?.some(characteristic => characteristic.uuid.toLowerCase() === uriChar));
        if (serviceWithUri) {
          return serviceWithUri.uuid.toLowerCase();
        }

        const discoveredUuids = new Set(services.map(service => service.uuid.toLowerCase()));
        const candidate = CANNES_BLE_CONFIG.REST_SERVICE_CANDIDATES.find(uuid => discoveredUuids.has(uuid.toLowerCase()));
        return candidate ?? CANNES_BLE_CONFIG.SERVICES.ZEISS_CUSTOM;
      }),
      catchError((error: unknown) => {
        this.log.debug('Service resolution failed - using default service UUID', { error });
        return of(CANNES_BLE_CONFIG.SERVICES.ZEISS_CUSTOM);
      }),
      tap(serviceUuid => {
        this.serviceUuidByDevice.set(deviceId, serviceUuid);
        this.log.debug('Resolved REST service for device', { deviceId, serviceUuid });
      })
    );
  }

  getServiceUuid(deviceId: string): string {
    return this.serviceUuid(deviceId);
  }

  private serviceUuid(deviceId: string): string {
    return this.serviceUuidByDevice.get(deviceId) ?? CANNES_BLE_CONFIG.SERVICES.ZEISS_CUSTOM;
  }

  get$<T>(deviceId: string, endpoint: string): Observable<T> {
    this.log.debug('GET request', { endpoint });
    return defer(() => this.executeGet$<T>(deviceId, endpoint)).pipe(
      retry({
        count: this.RETRY_COUNT,
        delay: (error: unknown, retryIndex: number) => this.recoverBeforeRetry$(deviceId, error, retryIndex),
      })
    );
  }

  put$<T>(deviceId: string, endpoint: string, body?: unknown): Observable<T> {
    if (this.log.isEnabled('debug')) {
      // Redact secrets (e.g. the AP passphrase) from the logged body — key-based log redaction cannot catch a secret that is the whole body.
      const loggedBody = /passphrase|password/i.test(endpoint) ? '<redacted>' : body;
      this.log.debug('PUT request', { endpoint, body: loggedBody });
    }
    return defer(() => this.executePut$<T>(deviceId, endpoint, body));
  }

  ensureNotificationsSetup$(deviceId: string): Observable<void> {
    return this.enableStatusNotifications$(deviceId);
  }

  // --- GET (with chunked continuation read) -------------------------------------------------

  private executeGet$<T>(deviceId: string, endpoint: string): Observable<T> {
    return this.enableStatusNotifications$(deviceId).pipe(
      tap(() => {
        this.log.debug('Notifications ready');
      }),
      switchMap(() => this.writeUri$(deviceId, endpoint)),
      tap(() => {
        this.log.debug('URI written');
      }),
      switchMap(() => this.readWithContinuation$(deviceId)),
      map(text => this.parseResponseText<T>(text))
    );
  }

  /**
   * Triggers GET and reads the entity body, following BODY_TRUNCATED continuation chunks
   * until the transfer completes, then concatenates all chunks into the full response text.
   */
  private readWithContinuation$(deviceId: string): Observable<string> {
    return this.readChunk$(deviceId).pipe(
      expand(result => (result.truncated ? this.readChunk$(deviceId) : EMPTY)),
      map(result => result.chunk),
      toArray(),
      map(chunks => chunks.join(''))
    );
  }

  private readChunk$(deviceId: string): Observable<{ chunk: string; truncated: boolean }> {
    const status$ = this.waitForStatus$(deviceId);
    const trigger$ = this.triggerRequest$(deviceId, 'GET').pipe(
      catchError((error: unknown) => {
        this.log.debug('GET trigger write failed (continuing to await notification)', { error });
        return of(void 0);
      }),
      ignoreElements()
    );

    return merge(status$, trigger$).pipe(
      timeout({ each: this.REQUEST_TIMEOUT_MS }),
      take(1),
      switchMap(statusView => {
        const status = this.parseStatus(statusView);
        if (!status.empty && (status.code < 200 || status.code >= 300)) {
          return throwError(() => new Error(`HTTP ${status.code}`));
        }
        return this.readEntityBodyText$(deviceId).pipe(map(chunk => ({ chunk, truncated: status.truncated })));
      })
    );
  }

  // --- PUT (with chunked write) -------------------------------------------------------------

  private executePut$<T>(deviceId: string, endpoint: string, body?: unknown): Observable<T> {
    const chunks = this.buildWriteChunks(body);

    return this.enableStatusNotifications$(deviceId).pipe(
      tap(() => {
        this.log.debug('Notifications ready');
      }),
      switchMap(() => this.writeUri$(deviceId, endpoint)),
      tap(() => {
        this.log.debug('URI written');
      }),
      switchMap(() => this.writeChunks$(deviceId, chunks)),
      // No caller consumes a PUT response body (all callers use put$<void>).
      map(() => undefined as T)
    );
  }

  /**
   * Splits the serialized body into sequential write chunks.
   * Bodies that fit in a single write use the firmware's non-Range path (no Range header);
   * larger bodies are split into `MAX_CHUNK_SIZE`-byte chunks each tagged with a Content-Range.
   */
  private buildWriteChunks(body: unknown): Array<WriteChunk> {
    if (body === undefined) {
      return [];
    }

    const bodyText = this.serializeBody(body);
    const bytes = this.textEncoder.encode(bodyText);
    const total = bytes.byteLength;

    if (total <= MAX_CHUNK_SIZE) {
      return [{ bytes, range: null }];
    }

    const chunks: Array<WriteChunk> = [];
    for (let start = 0; start < total; start += MAX_CHUNK_SIZE) {
      const end = Math.min(start + MAX_CHUNK_SIZE, total) - 1;
      chunks.push({ bytes: bytes.slice(start, end + 1), range: { start, end, total } });
    }

    this.log.debug('Body split into chunks', { chunkCount: chunks.length, totalBytes: total });
    return chunks;
  }

  private writeChunks$(deviceId: string, chunks: Array<WriteChunk>): Observable<void> {
    if (chunks.length === 0) {
      // Body-less PUT: trigger only.
      return this.writeChunkStep$(deviceId, null);
    }

    return from(chunks).pipe(
      concatMap(chunk => this.writeChunkStep$(deviceId, chunk)),
      last(),
      map(() => void 0)
    );
  }

  /**
   * Writes one chunk (optional Range header + entity body) then triggers PUT and awaits the
   * status notification. Intermediate chunks receive HTTP 202 | BODY_TRUNCATED acks; the final
   * chunk yields the real 2xx response.
   */
  private writeChunkStep$(deviceId: string, chunk: WriteChunk | null): Observable<void> {
    const writeHeader$ = chunk?.range ? this.writeHeaders$(deviceId, chunk.range) : of(void 0);
    const writeBody$ = chunk ? this.writeEntityBody$(deviceId, chunk.bytes) : of(void 0);

    // Write the Range header + entity body FIRST (fully awaited) BEFORE listening for status.
    //
    // This ordering is critical. The firmware/BLE stack emits a DUPLICATE of the previous
    // chunk's status notification (observed on-device: two identical `ca0008` notifications per
    // chunk). If the status listener is subscribed at the start of the chunk (i.e. merged with
    // the header/body writes), that stale duplicate is consumed as *this* chunk's response and
    // take(1) cancels the still-pending body/trigger writes. The result: the chunk writes only
    // its Range header, the firmware never advances receivedWriteSize_, and the next chunk's
    // Range fails validation with HTTP 400. By writing header+body first and only then
    // subscribing the status listener, the previous chunk's duplicate has already been dropped
    // (a plain Subject buffers nothing), so we correctly await this chunk's own acknowledgment.
    const prep$ = concat(writeHeader$, writeBody$).pipe(ignoreElements());

    // Only once the body is written do we listen for the status and fire the trigger together.
    // merge (not concat) avoids the race where the notification arrives before we subscribe.
    const triggerAndAwait$ = defer(() => {
      const status$ = this.waitForStatus$(deviceId);
      const trigger$ = this.triggerRequest$(deviceId, 'PUT').pipe(
        catchError((error: unknown) => {
          this.log.debug('PUT trigger write failed (continuing to await notification)', { error });
          return of(void 0);
        }),
        ignoreElements()
      );
      return merge(status$, trigger$).pipe(take(1));
    });

    return concat(prep$, triggerAndAwait$).pipe(
      timeout({ each: this.REQUEST_TIMEOUT_MS }),
      switchMap(statusView => {
        const status = this.parseStatus(statusView);
        if (!status.empty && (status.code < 200 || status.code >= 300)) {
          return throwError(() => new Error(`HTTP ${status.code}`));
        }
        if (chunk?.range) {
          this.log.debug('Chunk acknowledged', { start: chunk.range.start, end: chunk.range.end, total: chunk.range.total, status: status.code });
        }
        return of(void 0);
      })
    );
  }

  // --- Characteristic I/O ------------------------------------------------------------------

  // NOTE: every BLE I/O below is wrapped in defer() so the underlying BleClient promise starts
  // only on subscription. This is essential: concat()/merge() must serialize the header -> body ->
  // trigger writes in order. Firing them eagerly races the control-point trigger ahead of the
  // body/header, which desyncs the firmware's chunk state (the last chunk never completes).
  private writeUri$(deviceId: string, endpoint: string): Observable<void> {
    return defer(() =>
      from(BleClient.write(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.URI_ENDPOINT, this.stringToDataView(endpoint)))
    );
  }

  private writeHeaders$(deviceId: string, range: ContentRange): Observable<void> {
    return defer(() => {
      const headerJson = JSON.stringify({ Range: `bytes ${range.start}-${range.end}/${range.total}` });
      this.log.debug('Writing Range header', { header: headerJson });
      return from(BleClient.write(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.RESPONSE_HEADER, this.stringToDataView(headerJson)));
    });
  }

  private writeEntityBody$(deviceId: string, bytes: Uint8Array): Observable<void> {
    return defer(() => {
      this.log.debug('Writing entity body chunk', { bytes: bytes.byteLength });
      return from(BleClient.write(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.RESPONSE_BODY, this.bytesToDataView(bytes)));
    });
  }

  private triggerRequest$(deviceId: string, method: 'GET' | 'PUT'): Observable<void> {
    return defer(() => {
      const triggerByte = method === 'GET' ? CANNES_BLE_CONFIG.TRIGGERS.GET : CANNES_BLE_CONFIG.TRIGGERS.PUT;
      const triggerData = new DataView(new ArrayBuffer(1));
      triggerData.setUint8(0, triggerByte);

      this.log.debug('Triggering request', { method, triggerByte: `0x${triggerByte.toString(16)}` });
      return from(BleClient.write(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.CONTROL_POINT, triggerData));
    });
  }

  private readEntityBodyText$(deviceId: string): Observable<string> {
    return defer(() => from(BleClient.read(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.RESPONSE_BODY))).pipe(
      map(dataView => {
        // Decode the DataView directly so its byteOffset/byteLength are respected.
        const text = this.textDecoder.decode(dataView);
        // Size only: a chunk is partial JSON and cannot be redacted; the full body is logged once parsed.
        this.log.debug('Read body chunk', { bytes: dataView.byteLength });
        return text;
      })
    );
  }

  // --- Notifications -----------------------------------------------------------------------

  private enableStatusNotifications$(deviceId: string): Observable<void> {
    if (this.notificationStates.get(deviceId)) {
      this.log.debug('Notifications already active - reusing existing handler');
      return of(void 0);
    }

    const subject = this.getNotificationSubject(deviceId);
    this.log.debug('Setting up STATUS_CODE notifications for first time');
    return from(
      BleClient.startNotifications(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.STATUS_CODE, (dataView: DataView) => {
        subject.next(dataView);
      })
    ).pipe(
      tap(() => {
        this.notificationStates.set(deviceId, true);
        this.log.debug('Notifications enabled and will remain active');
      })
    );
  }

  private getNotificationSubject(deviceId: string): Subject<DataView> {
    let subject = this.notificationSubjects.get(deviceId);
    if (!subject || subject.closed) {
      subject = new Subject<DataView>();
      this.notificationSubjects.set(deviceId, subject);
    }
    return subject;
  }

  private waitForStatus$(deviceId: string): Observable<DataView> {
    return this.getNotificationSubject(deviceId).pipe(take(1));
  }

  private parseStatus(value: DataView): ParsedStatus {
    if (value.byteLength === 0) {
      // Device sends an empty notification for some PUT responses as an implicit HTTP 200 OK.
      this.log.debug('Empty status notification - treating as success');
      return { code: 200, dataStatus: DATA_STATUS.BODY_RECEIVED, truncated: false, empty: true };
    }

    const code = value.byteLength >= 2 ? value.getUint8(0) | (value.getUint8(1) << 8) : value.getUint8(0);
    const dataStatus = value.byteLength >= 3 ? value.getUint8(2) : DATA_STATUS.BODY_RECEIVED;
    const truncated = (dataStatus & DATA_STATUS.BODY_TRUNCATED) !== 0;

    if (this.log.isEnabled('debug')) {
      const raw = Array.from(new Uint8Array(value.buffer, value.byteOffset, value.byteLength))
        .map(b => b.toString(16).padStart(2, '0'))
        .join(' ');
      this.log.debug('Status notification', { status: code, dataStatus: `0x${dataStatus.toString(16).padStart(2, '0')}`, truncated, raw });
    }

    return { code, dataStatus, truncated, empty: false };
  }

  private stopNotifications(deviceId: string): void {
    BleClient.stopNotifications(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.STATUS_CODE).catch(error => {
      this.log.debug('Failed to stop notifications', { error });
    });
  }

  // --- Serialization helpers ---------------------------------------------------------------

  private serializeBody(body: unknown): string {
    if (body instanceof Uint8Array) {
      return JSON.stringify(Array.from(body));
    }
    if (typeof body === 'string') {
      return body;
    }
    return JSON.stringify(body);
  }

  private parseResponseText<T>(text: string): T {
    let parsed: T;
    try {
      parsed = JSON.parse(text) as T;
    } catch {
      parsed = text as T;
    }
    // Log the parsed body, not the raw text: only object keys go through the key-based redaction
    // (e.g. a passphrase field in a Wi-Fi config response).
    this.log.debug('Full response', { chars: text.length, body: parsed });
    return parsed;
  }

  private stringToDataView(text: string): DataView {
    const bytes = this.textEncoder.encode(text);
    return this.bytesToDataView(bytes);
  }

  /** Copies the bytes into a fresh, tightly-sized ArrayBuffer so the plugin sees an exact-length value. */
  private bytesToDataView(bytes: Uint8Array): DataView {
    const buffer = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(buffer).set(bytes);
    return new DataView(buffer);
  }

  // --- Recovery & cleanup ------------------------------------------------------------------

  private recoverBeforeRetry$(deviceId: string, error: unknown, retryIndex: number): Observable<unknown> {
    this.log.debug('Request failed - attempting recovery', { retryIndex, retryCount: this.RETRY_COUNT, error });

    return from(BleClient.getConnectedDevices([this.serviceUuid(deviceId)])).pipe(
      switchMap(devices => {
        const stillConnected = devices.some(d => d.deviceId === deviceId);
        if (!stillConnected) {
          this.log.debug('Device not in getConnectedDevices - cannot recover, surfacing error');
          return throwError(() => new Error('Device disconnected'));
        }
        this.log.debug('Device still connected - resetting notification subscription (possible handle/CCCD drift)');
        return this.resetNotificationSubscription$(deviceId).pipe(switchMap(() => timer(this.RETRY_DELAY_MS)));
      })
    );
  }

  private resetNotificationSubscription$(deviceId: string): Observable<void> {
    const hadNotifications = this.notificationStates.get(deviceId) === true;

    const stop$: Observable<void> = hadNotifications
      ? from(BleClient.stopNotifications(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.STATUS_CODE)).pipe(
          catchError((error: unknown) => {
            this.log.debug('stopNotifications during recovery failed (continuing)', { error });
            return of(undefined);
          }),
          map(() => void 0)
        )
      : of(void 0);

    return stop$.pipe(
      tap(() => {
        this.notificationStates.delete(deviceId);

        const staleSubject = this.notificationSubjects.get(deviceId);
        if (staleSubject && !staleSubject.closed) {
          staleSubject.complete();
        }
        this.notificationSubjects.delete(deviceId);

        this.log.debug('Notification subscription state cleared - will re-subscribe on next request');
      })
    );
  }

  resetDeviceNotificationState(deviceId: string): void {
    if (this.notificationStates.get(deviceId)) {
      this.stopNotifications(deviceId);
    }
    this.notificationStates.delete(deviceId);

    const subject = this.notificationSubjects.get(deviceId);
    if (subject && !subject.closed) {
      subject.error(new Error('Device disconnected during bonding'));
    }
    this.notificationSubjects.delete(deviceId);
  }

  cleanupDevice(deviceId: string): void {
    this.log.debug('Cleaning up device state', { deviceId });

    if (this.notificationStates.get(deviceId)) {
      this.log.debug('Stopping notifications for device', { deviceId });
      this.stopNotifications(deviceId);
    }
    this.notificationStates.delete(deviceId);

    const subject = this.notificationSubjects.get(deviceId);
    if (subject && !subject.closed) {
      subject.error(new Error('Device disconnected'));
    }
    this.notificationSubjects.delete(deviceId);

    this.serviceUuidByDevice.delete(deviceId);
  }
}
/* eslint-enable @smarttools/rxjs/finnish */
